import React, { useState, useEffect, useCallback } from "react";
import { X, Phone, RefreshCw, Trash2, Save, ShieldAlert } from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";

interface WhatsAppIntegrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const WhatsAppIntegrationModal: React.FC<WhatsAppIntegrationModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const [phones, setPhones] = useState<string[]>([]);
    const [currentPhone, setCurrentPhone] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Confirmation dialog states
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
    const [confirmTitle, setConfirmTitle] = useState("");
    const [confirmDesc, setConfirmDesc] = useState("");

    /*
    // Session states
    const [sessionStatus, setSessionStatus] = useState<string>("not started");
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [sessionStarting, setSessionStarting] = useState(false);
    const [sessionClearing, setSessionClearing] = useState(false);

    // Test send states
    const [testPhone, setTestPhone] = useState("");
    const [testMsg, setTestMsg] = useState("Hello from the video monitoring system!");
    const [isSendingTest, setIsSendingTest] = useState(false);
    */

    // const pollTimerRef = useRef<any>(null);

    const loadSavedIntegration = useCallback(async () => {
        try {
            const response = await baseapi.get("/whatsapp/integration");
            if (response.data?.status && response.data?.data) {
                // If data is returned inside "data" array or single object
                const item = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                const savedPhone = item?.phone || item?.integration_data?.phone || "";
                if (savedPhone) {
                    let phoneArray: string[] = [];
                    if (Array.isArray(savedPhone)) {
                        phoneArray = savedPhone;
                    } else if (typeof savedPhone === 'string') {
                        phoneArray = savedPhone.split(',').filter((p: string) => p.trim() !== '');
                    }

                    // Strip +91 for display so user only sees 10 digits
                    phoneArray = phoneArray.map(p => {
                        let c = p.replace(/\D/g, "");
                        if (c.length === 12 && c.startsWith("91")) return c.substring(2);
                        return c;
                    });

                    setPhones(phoneArray);
                    // setTestPhone(phoneArray[0] || "");
                }
            }
        } catch (error) {
            console.error("Failed to load saved WhatsApp integration:", error);
        }
    }, []);

    /*
    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const checkStatus = useCallback(async (quiet = true) => {
        if (!quiet) setStatusLoading(true);
        try {
            const response = await baseapi.get("/whatsapp/session/status");
            if (response.data) {
                const s = response.data.session_status || response.data.status || "error";
                setSessionStatus(s);
                if (s === "authenticated") {
                    stopPolling();
                    setQrCode(null);
                    if (!quiet) showSuccess("WhatsApp session is authenticated and ready!");
                } else if (response.data.qr_code) {
                    setQrCode(response.data.qr_code);
                }
            }
        } catch (error: any) {
            console.error("Check status error:", error);
            if (!quiet) showError(error.response?.data?.message || "Failed to check session status");
        } finally {
            if (!quiet) setStatusLoading(false);
        }
    }, [stopPolling]);

    const startPolling = useCallback(() => {
        stopPolling();
        // Poll every 10 seconds to keep QR code fresh and check for authentication
        pollTimerRef.current = setInterval(() => {
            checkStatus(true);
        }, 10000);
    }, [checkStatus, stopPolling]);
    */

    useEffect(() => {
        if (isOpen) {
            loadSavedIntegration();
        }
    }, [isOpen, loadSavedIntegration]);

    if (!isOpen) return null;

    const formatPhoneForBackend = (num: string) => {
        let cleaned = num.replace(/\D/g, ""); // Remove all non-digits
        if (cleaned.length === 10) {
            return "91" + cleaned;
        }
        if (cleaned.length === 12 && cleaned.startsWith("91")) {
            return cleaned;
        }
        return "91" + cleaned;
    };

    const handleSaveIntegration = async () => {
        let allPhones = [...phones];
        const currentVal = currentPhone.trim();
        if (currentVal) {
            if (currentVal.replace(/\D/g, "").length !== 10) {
                showError("Please enter exactly 10 digits");
                return;
            }
            allPhones.push(currentVal);
            setCurrentPhone("");
            setPhones(allPhones);
        }

        if (allPhones.length === 0) {
            showError("At least one phone number is required");
            return;
        }
        const formattedPhones = allPhones.map(formatPhoneForBackend).join(',');
        setIsSaving(true);
        try {
            const response = await baseapi.post("/whatsapp/connect", { phone: formattedPhones });
            if (response.data?.status || response.status === 200) {
                showSuccess("WhatsApp integration saved successfully");
                // setTestPhone(allPhones[0] || "");
                onSuccess();
                onClose();
            } else {
                showError(response.data?.message || "Failed to save integration");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Error connecting integration");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveIntegration = () => {
        setConfirmTitle("Remove Integration?");
        setConfirmDesc("Are you sure you want to remove the WhatsApp integration? This will disconnect your setup.");
        setConfirmAction(() => executeRemoveIntegration);
        setIsConfirmOpen(true);
    };

    const executeRemoveIntegration = async () => {
        setIsDeleting(true);
        try {
            const response = await baseapi.delete("/whatsapp/integration");
            if (response.data?.status || response.status === 200) {
                showSuccess("WhatsApp integration removed successfully");
                setPhones([]);
                setCurrentPhone("");
                // setTestPhone("");
                onSuccess();
            } else {
                showError(response.data?.message || "Failed to remove integration");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Error removing integration");
        } finally {
            setIsDeleting(false);
        }
    };

    /*
    const handleStartSession = async () => {
        if (phones.length === 0) {
            showError("Phone number is required. Please save a phone number in Recepiant Details first.");
            return;
        }
        stopPolling();
        setSessionStarting(true);
        setQrCode(null);
        try {
            const response = await baseapi.post("/whatsapp/session/start");
            if (response.data) {
                if (response.data.authenticated) {
                    setSessionStatus("authenticated");
                    showSuccess("Already authenticated!");
                } else if (response.data.qr_code) {
                    setSessionStatus("pending_qr");
                    setQrCode(response.data.qr_code);
                    startPolling();
                } else {
                    const s = response.data.session_status || "starting";
                    setSessionStatus(s);
                    startPolling();
                }
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Error starting WhatsApp session");
        } finally {
            setSessionStarting(false);
        }
    };

    const handleClearSession = () => {
        setConfirmTitle("Clear Session?");
        setConfirmDesc("Clear WhatsApp session? You will need to link your device again.");
        setConfirmAction(() => executeClearSession);
        setIsConfirmOpen(true);
    };

    const executeClearSession = async () => {
        stopPolling();
        setSessionClearing(true);
        try {
            const response = await baseapi.delete("/whatsapp/session/clear");
            if (response.data) {
                setSessionStatus("not started");
                setQrCode(null);
                showSuccess("WhatsApp Web session cleared successfully");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Error clearing session");
        } finally {
            setSessionClearing(false);
        }
    };

    const handleTestSend = async () => {
        if (!testPhone.trim()) {
            showError("Test phone number is required");
            return;
        }
        const formattedTestPhone = formatPhoneForBackend(testPhone);
        setIsSendingTest(true);
        try {
            const response = await baseapi.post("/whatsapp/test", {
                phone: formattedTestPhone,
                message: testMsg.trim()
            });
            if (response.data) {
                showSuccess("Test notification sent successfully!");
                setTestPhone(formattedTestPhone);
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Failed to send test message");
        } finally {
            setIsSendingTest(false);
        }
    };

    const getStatusStyle = (status: string) => {
        const s = status.toLowerCase();
        if (s.includes("auth")) {
            return "bg-emerald-50 text-emerald-700 border-emerald-150";
        }
        if (s.includes("qr") || s.includes("pend") || s.includes("start")) {
            return "bg-amber-50 text-amber-700 border-amber-150";
        }
        return "bg-rose-50 text-rose-700 border-rose-150";
    };
    */

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-52 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300 p-4"
            onClick={handleBackdropClick}
        >
            <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-300 flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-slate-50 px-5 py-3.5 flex items-center justify-between shrink-0 border-b border-slate-100 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                            <img src="/whatsapp.svg" alt="WhatsApp" className="w-6 h-6 object-contain" />
                        </div>
                        <div>
                            <h2 className="text-sm font-black text-slate-800 tracking-tight uppercase">WhatsApp Integration</h2>
                            <p className="text-emerald-600 text-[8px] font-black uppercase tracking-[0.2em] leading-none mt-1">
                                Notifications Engine
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-800 transition-all p-1.5 rounded-lg hover:bg-slate-100 border border-transparent hover:border-slate-200/50 active:scale-95"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll">
                    {/* SECTION 1: Connect Integration */}
                    <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
                        <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-[#422568]" />
                            <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-wider">
                                1. Recepiant Details
                            </h3>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between mb-1.5 ml-0.5">
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                    WhatsApp Phone Number(s) <span className="text-rose-500">*</span>
                                </label>
                                <span className="px-1.5 py-0.5 bg-[#422568]/5 text-[#422568] border border-[#422568]/15 rounded text-[8px] font-black uppercase tracking-wider">
                                    Type 10 digits & press Enter
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2 p-2 bg-white rounded-lg border border-slate-200 focus-within:ring-2 focus-within:ring-[#422568]/10 focus-within:border-[#422568] transition-all">
                                {phones.map((p, idx) => (
                                    <span key={idx} className="flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs font-semibold">
                                        {p}
                                        <button onClick={() => setPhones(phones.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-500">
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                                <input
                                    type="tel"
                                    placeholder={phones.length === 0 ? "9214567890" : ""}
                                    className="flex-1 min-w-[120px] bg-transparent outline-none text-xs font-semibold text-slate-800 placeholder:text-slate-400"
                                    value={currentPhone}
                                    onChange={(e) => setCurrentPhone(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const val = currentPhone.trim();
                                            if (val) {
                                                if (val.replace(/\D/g, "").length !== 10) {
                                                    showError("Please enter exactly 10 digits");
                                                    return;
                                                }
                                                setPhones([...phones, val]);
                                                setCurrentPhone("");
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={handleSaveIntegration}
                                disabled={isSaving}
                                className="flex-grow py-2 bg-[#422568] hover:bg-[#422568] text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-1"
                            >
                                {isSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Save Phone
                            </button>
                            <button
                                onClick={handleRemoveIntegration}
                                disabled={isDeleting}
                                className="px-3 py-2 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-1"
                            >
                                {isDeleting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                Remove
                            </button>
                        </div>
                    </div>

                    {/* SECTION 2: Session Management */}
                    {/*
                    <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <QrCode className="w-3.5 h-3.5 text-emerald-600" />
                                <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-wider">
                                    2. Web Session Manager
                                </h3>
                            </div>
                            <span className={`px-1.5 py-0.5 rounded-full text-[7.5px] font-black uppercase tracking-wider border shadow-sm ${getStatusStyle(sessionStatus)}`}>
                                {sessionStatus.replace("_", " ")}
                            </span>
                        </div>

                        <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                            Control the server browser session to pair this dashboard with your WhatsApp mobile application.
                        </p>

                        <div className="grid grid-cols-3 gap-2">
                            <button
                                onClick={handleStartSession}
                                disabled={sessionStarting}
                                className="py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[9px] font-black uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-1"
                            >
                                {sessionStarting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <QrCode className="w-3 h-3" />}
                                Get QR Code
                            </button>
                            <button
                                onClick={() => checkStatus(false)}
                                disabled={statusLoading}
                                className="py-1.5 bg-[#422568] hover:bg-[#422568] text-white rounded-lg text-[9px] font-black uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-1"
                            >
                                {statusLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                Check Status
                            </button>
                            <button
                                onClick={handleClearSession}
                                disabled={sessionClearing}
                                className="py-1.5 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-1"
                            >
                                {sessionClearing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3 h-3" />}
                                Clear Session
                            </button>
                        </div>

                        // QR Image Frame
                    {qrCode && (
                        <div className="flex flex-col items-center justify-center border border-dashed border-slate-200 bg-white rounded-xl p-3 animate-in fade-in duration-300">
                            <div className="border-2 border-emerald-500 rounded-lg p-0.5 bg-white">
                                <img
                                    src={`data:image/png;base64,${qrCode}`}
                                    alt="Scan QR"
                                    className="w-[140px] h-[140px] object-contain image-rendering-pixelated"
                                />
                            </div>
                            <div className="flex gap-1.5 items-start mt-2 text-slate-400 max-w-[260px]">
                                <Smartphone className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                <p className="text-[9px] text-center font-bold leading-normal">
                                    Open WhatsApp on your phone → ⋮ → Linked Devices → Link a Device → Scan QR code
                                </p>
                            </div>

                        </div>
                    )}
                </div>

                <div className="">
                    <p className="text-[11px] text-center text-red-500 font-bold leading-normal">Note: After scanning the QR Code it might take upto 5 minutes to send the notifications</p>
                </div>

                // SECTION 3: Send Test Message
                <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
                    <div className="flex items-center gap-2">
                        <Send className="w-3.5 h-3.5 text-[#422568]" />
                        <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-wider">
                            3. Dispatch Test Alert
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block ml-0.5">
                                Recipient Phone Number
                            </label>
                            <input
                                type="tel"
                                placeholder="+919214567890"
                                className="w-full pl-3 pr-3 py-2 bg-white text-slate-800 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] outline-none text-xs font-semibold transition-all"
                                value={testPhone}
                                onChange={(e) => setTestPhone(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block ml-0.5">
                                Message Copy
                            </label>
                            <input
                                type="text"
                                placeholder="Hello from the video monitoring system!"
                                className="w-full pl-3 pr-3 py-2 bg-white text-slate-800 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] outline-none text-xs font-semibold transition-all"
                                value={testMsg}
                                onChange={(e) => setTestMsg(e.target.value)}
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleTestSend}
                        disabled={isSendingTest}
                        className="w-full py-2 bg-[#422568] hover:bg-[#422568] text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-1"
                    >
                        {isSendingTest ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Send Test Message
                    </button>
                </div>
                    */}
                </div>

                {/* Footer buttons */}
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 transition-all active:scale-95"
                    >
                        Done
                    </button>
                </div>
            </div>

            {/* Custom Confirmation Modal */}
            {
                isConfirmOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden text-slate-800">
                            <div className="p-6 text-center">
                                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-rose-100 shadow-sm">
                                    <ShieldAlert className="w-6 h-6 text-rose-500" />
                                </div>
                                <h2 className="text-base font-black text-slate-800 mb-1.5 uppercase tracking-tight">{confirmTitle}</h2>
                                <p className="text-[11px] text-slate-500 font-semibold leading-relaxed">
                                    {confirmDesc}
                                </p>
                            </div>
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-2">
                                <button
                                    onClick={() => {
                                        setIsConfirmOpen(false);
                                        setConfirmAction(null);
                                    }}
                                    className="flex-1 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 transition-all active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (confirmAction) confirmAction();
                                        setIsConfirmOpen(false);
                                        setConfirmAction(null);
                                    }}
                                    className="flex-1 py-2 bg-[#422568] hover:bg-[#422568] text-white rounded-lg text-xs font-black uppercase tracking-wider transition-all active:scale-95"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};
